import { client } from 'Utils/apollo/client';
import {
  GetSubrentalsDocument,
  GetSubrentalsQuery,
  GetSubrentalsQueryVariables,
  SubrentalProductTypeEnum,
  SubrentalSortFieldEnum,
  SubrentalSourceEnum,
} from '../types/graphql';

// Orders > Subrentals reads the GraphQL getSubrentals query instead of the legacy
// REST /api/rental_inventories/paginated?filter=subrentals_all. Rows are mapped back
// to the snake_case shape the REST endpoint returned, so the table, row grouping and
// row links keep working unchanged.

type SubrentalRow = GetSubrentalsQuery['getSubrentals']['subrentals'][number];

export interface SubrentalsListParams {
  numberPer?: number;
  startDate?: Date | string | null;
  endDate?: Date | string | null;
  query?: string;
  zone?: number;
  page?: number | string;
  itemId?: number | string;
  productType?: string;
  subRentalSource?: string;
  sortField?: string;
  sortDirection?: string;
  prioritize_sticky?: boolean;
  multilocationAllLocations?: boolean;
}

const PRODUCT_TYPES = Object.values(SubrentalProductTypeEnum) as string[];
const SORT_FIELDS = Object.values(SubrentalSortFieldEnum) as string[];
const SOURCES = Object.values(SubrentalSourceEnum) as string[];

// A malformed date in the URL (?startDate=garbage) becomes an Invalid Date upstream;
// drop it rather than throw, as the REST call did by sending null.
const toDateParam = (value?: Date | string | null) => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value.toISOString();
  }
  return value || undefined;
};

const toPositiveInt = (value?: number | string) => {
  const parsed = parseInt(String(value), 10);
  return parsed > 0 ? parsed : undefined;
};

export const subrentalsQueryVariables = (
  params: SubrentalsListParams
): GetSubrentalsQueryVariables => {
  // The item autocomplete can also offer bundles, which are never sub-rented themselves.
  const hasItemFilter =
    !!params.itemId && PRODUCT_TYPES.includes(params.productType || '');

  return {
    startDate: toDateParam(params.startDate),
    endDate: toDateParam(params.endDate),
    timeZoneOffset: Number.isFinite(params.zone)
      ? String(params.zone)
      : undefined,
    query: params.query || undefined,
    itemId: hasItemFilter ? String(params.itemId) : undefined,
    productType: hasItemFilter
      ? (params.productType as SubrentalProductTypeEnum)
      : undefined,
    sources: (params.subRentalSource || '')
      .split(',')
      .filter((source) => SOURCES.includes(source)) as SubrentalSourceEnum[],
    sortField: SORT_FIELDS.includes(params.sortField || '')
      ? (params.sortField as SubrentalSortFieldEnum)
      : undefined,
    sortDirection: params.sortDirection || undefined,
    page: toPositiveInt(params.page),
    perPage: toPositiveInt(params.numberPer),
    prioritizeSticky: !!params.prioritize_sticky,
    multilocationAllLocations: !!params.multilocationAllLocations,
  };
};

export const toRentalInventoryRow = (row: SubrentalRow) => ({
  id: Number(row.recordId),
  name: row.name,
  // Current catalog name: SubrentalsAllRow shows it, with a "Formerly:" toggle when it differs from name.
  inventory_name: row.currentName,
  quantity: row.quantity,
  type: row.type,
  has_shortage: row.hasShortage,
  parent_inventory: row.parentInventoryId
    ? { id: Number(row.parentInventoryId) }
    : null,
  parent_rental: row.rental
    ? {
        id: Number(row.rental.id),
        token: row.rental.token,
        name: row.rental.name,
        status: row.rental.status,
        location_id: row.rental.locationId,
      }
    : null,
  rental_schedule: { offShelfAt: row.offShelfAt, onShelfAt: row.onShelfAt },
  sub_rental: {
    supplier: row.supplierName,
    customer_name: row.customerName,
    contact: row.contact,
    expense: row.expense,
    source: row.source,
  },
});

export const fetchSubrentalsList = async (params: SubrentalsListParams) => {
  const { data } = await client.query<
    GetSubrentalsQuery,
    GetSubrentalsQueryVariables
  >({
    query: GetSubrentalsDocument,
    variables: subrentalsQueryVariables(params),
    fetchPolicy: 'network-only',
  });
  const { subrentals, metadata } = data.getSubrentals;

  return {
    rentalInventories: subrentals.map(toRentalInventoryRow),
    pagination: {
      per_page: metadata.limitValue,
      total_pages: metadata.totalPages,
      total_objects: metadata.totalCount,
      page: metadata.currentPage,
    },
  };
};
